/**
 * Widget V2 - ErrorBoundary Component
 *
 * Catches rendering errors and displays fallback UI.
 */

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * ErrorBoundary class component
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Could send to error reporting service here
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });

    // If onReset callback provided, call it
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: this.props.fullPage ? "60vh" : "12.5rem",
            p: 3,
            textAlign: "center"
          }}
        >
          <Box
            sx={{
              width: "5rem",
              height: "5rem",
              borderRadius: "50%",
              bgcolor: "error.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: "2.5rem", color: "error.main" }} />
          </Box>

          <Typography variant="h6" gutterBottom>
            Something went wrong
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ maxWidth: "25rem", mb: 3 }}>
            {this.props.message || "We encountered an unexpected error. Please try again."}
          </Typography>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <Box
              sx={{
                bgcolor: "grey.100",
                p: 2,
                borderRadius: 1,
                mb: 3,
                maxWidth: "100%",
                overflow: "auto"
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  m: 0
                }}
              >
                {this.state.error.toString()}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={this.handleReset}>
              Try Again
            </Button>
            <Button variant="contained" onClick={this.handleReload}>
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
